import {
    CancelIcon,
    CircleDashed,
    CircleHalf,
    InfoStatusVariant,
    LoadingCloseToHalf,
    LoadingEmpty,
    LoadingFull,
    LoadingOneQuarter,
    LoadingThreeQuarters,
} from 'ComponentLibrary'
import { Check, Circle, Icon as ReactFeatherIcon, MinusCircle } from 'react-feather'
import { useTheme } from '@mui/material'
import { OrderStatus } from 'ComponentLibrary/src/common/order-status'
import { Icon } from '@phosphor-icons/react'

export type StatusVariant = 'square' | 'circle'

type OrderStatusState = {
    label: string
    longLabel: string
    textColor: string
    square: { icon: Icon | ReactFeatherIcon; color: string }
    circle: { icon: Icon | ReactFeatherIcon; color: string }
    infoStatus: InfoStatusVariant
}

export const useOrderStatus = () => {
    const theme = useTheme()

    const getOrderStatusIcon = (state: OrderStatus, variant: StatusVariant) => ORDER_STATUS_STATES[state][variant].icon
    const getOrderStatusIconColor = (state: OrderStatus, variant: StatusVariant) => ORDER_STATUS_STATES[state][variant].color
    const getOrderStatusText = (state: OrderStatus) => ORDER_STATUS_STATES[state].label
    const getOrderStatusLongText = (state: OrderStatus) => ORDER_STATUS_STATES[state].longLabel
    const getOrderStatusTextColor = (state: OrderStatus) => ORDER_STATUS_STATES[state].textColor
    const getOrderStatusInfoVariant = (state: OrderStatus) => ORDER_STATUS_STATES[state].infoStatus

    const ORDER_STATUS_STATES: { [K in OrderStatus]: OrderStatusState } = {
        open: {
            label: 'Auftrag eingegangen',
            longLabel: 'Ihr Auftrag wird in Kürze bearbeitet',
            textColor: '#754D88',
            square: {
                icon: Circle,
                color: theme.palette.status.blue,
            },
            circle: {
                icon: LoadingEmpty,
                color: 'transparent',
            },
            infoStatus: 'ordinary',
        },
        accepted: {
            label: 'Angenommen',
            longLabel: 'Der Auftrag wurde angenommen',
            textColor: '#4F5785',
            square: {
                icon: Check,
                color: theme.palette.status.blue,
            },
            circle: {
                icon: LoadingOneQuarter,
                color: '#7D8AD2',
            },
            infoStatus: 'ordinary',
        },
        in_communication_with_owner: {
            label: 'In Kommunikation mit Eigentümer',
            longLabel: 'Wir sind in Kommunikation mit dem Eigentümer des Objekts.',
            textColor: '#45678F',
            square: {
                icon: CircleHalf,
                color: theme.palette.status.yellow,
            },
            circle: {
                icon: LoadingCloseToHalf,
                color: '#74A8E4',
            },
            infoStatus: 'warning',
        },
        inspection_scheduled: {
            label: 'Besichtigung vereinbart',
            longLabel: 'Ein Termin für die Besichtigung wurde vereinbart.',
            textColor: '#45678F',
            square: {
                icon: CircleHalf,
                color: theme.palette.status.yellow,
            },
            circle: {
                icon: LoadingCloseToHalf,
                color: '#74A8E4',
            },
            infoStatus: 'warning',
        },
        in_progress: {
            label: 'Bearbeitung',
            longLabel: 'Gutachten ist in Bearbeitung',
            textColor: '#45678F',
            square: {
                icon: CircleHalf,
                color: theme.palette.status.yellow,
            },
            circle: {
                icon: LoadingCloseToHalf,
                color: '#74A8E4',
            },
            infoStatus: 'warning',
        },
        in_review: {
            label: 'Entwurf prüfen',
            longLabel: 'Neuer Gutachtenentwurf bereit zur Prüfung',
            textColor: '#45678F',
            square: {
                icon: CircleHalf,
                color: theme.palette.status.yellow,
            },
            circle: {
                icon: LoadingThreeQuarters,
                color: '#74A8E4',
            },
            infoStatus: 'warning',
        },
        done: {
            label: 'Abgeschlossen',
            longLabel: 'Auftrag abgeschlossen',
            textColor: theme.palette.status.green,
            square: {
                icon: Check,
                color: theme.palette.status.green,
            },
            circle: {
                icon: LoadingFull,
                color: theme.palette.status.green,
            },
            infoStatus: 'positive',
        },
        blocked: {
            label: 'Blockiert',
            longLabel: 'Auftrag blockiert',
            textColor: theme.palette.status.red,
            square: {
                icon: MinusCircle,
                color: theme.palette.status.red,
            },
            circle: {
                icon: MinusCircle,
                color: theme.palette.status.red,
            },
            infoStatus: 'negative',
        },
        on_hold: {
            label: 'Pausiert',
            longLabel: 'Auftrag pausiert',
            textColor: '#e56f46',
            square: {
                icon: CircleDashed,
                color: theme.palette.status.red,
            },
            circle: {
                icon: LoadingCloseToHalf,
                color: theme.palette.status.red,
            },
            infoStatus: 'negative',
        },
        // This should never be shown anywhere in the client portal
        temp_hide_in_portal: {
            label: 'Versteckt',
            longLabel: 'Auftrag wird nur intern angezeigt',
            textColor: theme.palette.status.red,
            square: {
                icon: CircleDashed,
                color: theme.palette.status.red,
            },
            circle: {
                icon: LoadingFull,
                color: theme.palette.status.red,
            },
            infoStatus: 'negative',
        },
        canceled: {
            label: 'Abgebrochen',
            longLabel: 'Auftrag storniert',
            textColor: theme.palette.status.red,
            square: {
                icon: CancelIcon,
                color: theme.palette.status.red,
            },
            circle: {
                icon: LoadingFull,
                color: theme.palette.status.red,
            },
            infoStatus: 'negative',
        },
    }

    return {
        getOrderStatusText,
        getOrderStatusTextColor,
        getOrderStatusIconColor,
        getOrderStatusIcon,
        getOrderStatusLongText,
        getOrderStatusInfoVariant,
        ORDER_STATUS_STATES,
    }
}
